import { StorageReportEntry } from '@billing/app/api'
import { useBillingServiceStore } from '@billing/AppProvider'
import { CircularProgress, Stack } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useEffect, useMemo } from 'react'
import { ReportSummaryCard } from '../reportSummaryCard/ReportSummaryCard'

export const StorageReport = observer(() => {
  const { storageReportUIStore } = useBillingServiceStore()
  const { t } = useTranslate()

  const dataExportConfig = useMemo<IDataExportConfig<StorageReportEntry>>(
    () => ({
      containerNumber: { header: t('containerNumber', 'Container Number'), type: 'string' },
      operator: { header: t('operator', 'Operator'), type: 'string' },
      customerAbbreviation: { header: t('customer', 'Customer'), type: 'string' },
      isoCode: { header: t('isoCode', 'ISO Code'), type: 'string' },
      direction: { header: t('direction', 'Direction'), type: 'string' },
      isEmpty: { header: t('status', 'Status'), type: 'string' },
      daysOnTerminal: { header: t('days', 'Days'), type: 'number' },
      in: { header: t('inDate', 'In'), type: 'date' },
      out: { header: t('outDate', 'Out'), type: 'date' },
      isDangerous: { header: t('dangerous', 'Dangerous'), type: 'string' },
      isReefer: { header: t('reefer', 'Reefer'), type: 'string' },
      isDamaged: { header: t('damaged', 'Damaged'), type: 'string' },
    }),
    [t],
  )

  const columns = useMemo<MRT_ColumnDef<StorageReportEntry>[]>(
    () => [
      {
        id: 'containerNumber',
        header: t('containerNumber', 'Container Number'),
        accessorFn: row => row.containerNumber,
        size: 200,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'operator',
        header: t('operator', 'Operator'),
        accessorFn: row => row.operator,
        size: 130,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'customerAbbreviation',
        header: t('customer', 'Customer'),
        accessorFn: row => row.customerAbbreviation,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isoCode',
        header: t('isoCode', 'ISO Code'),
        accessorFn: row => row.isoCode,
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isEmpty',
        header: t('status', 'Status'),
        accessorFn: row => (row.isEmpty ? t('empty', 'Empty') : t('full', 'Full')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'daysOnTerminal',
        header: t('days', 'Days'),
        accessorFn: row => row.daysOnTerminal,
        size: 90,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'in',
        header: t('in', 'In'),
        accessorFn: row => row.in,
        ...tomTableColumnFilterPresets.dateOptional(),
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
      },
      {
        id: 'out',
        header: t('out', 'Out'),
        accessorFn: row => row.out,
        ...tomTableColumnFilterPresets.dateOptional(),
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
      },
      {
        id: 'inboundVoyage',
        header: t('inboundVoyage', 'In. Voyage'),
        accessorFn: row => row.inboundVoyage,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'outboundVoyage',
        header: t('outboundVoyage', 'Out. Voyage'),
        accessorFn: row => row.outboundVoyage,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'weight',
        header: t('weight', 'Weight'),
        accessorFn: row => row.weight,
        Cell: ({ cell }) => (cell.getValue<number>() ? `${cell.getValue<number>()} kg` : '-'),
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'isDangerous',
        header: t('dangerous', 'Dangerous'),
        accessorFn: row => row.isDangerous,
        size: 100,
        Cell: ({ cell }) => (cell.getValue<boolean>() ? t('yes', 'Yes') : t('no', 'No')),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isReefer',
        header: t('reefer', 'Reefer'),
        accessorFn: row => (row.isReefer ? t('yes', 'Yes') : t('no', 'No')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isDamaged',
        header: t('damaged', 'Damaged'),
        accessorFn: row => (row.isDamaged ? t('yes', 'Yes') : t('no', 'No')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'tempControl',
        header: t('tempControl', 'Temp. Control'),
        accessorFn: row => (row.tempControl ? t('yes', 'Yes') : t('no', 'No')),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isOOG',
        header: t('oog', 'OOG'),
        accessorFn: row => (row.isOOG ? t('yes', 'Yes') : t('no', 'No')),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const defaultOptions = useDefaultMRTOptions<StorageReportEntry>(undefined, {
    enableDataExport: true,
    title: t('storageReport', 'Storage Report'),
  })

  const { savedPreferences, onPreferencesChange } =
    useTablePreferencesConfig('billing/storage-report')

  const table = useTomTable(
    {
      columns,
      data: storageReportUIStore.storageEntries,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      enableRowActions: false,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: [...(defaultOptions.initialState?.columnPinning?.left ?? []), 'containerNumber'],
        },
      },
      muiTableContainerProps: {
        sx: {
          minHeight: '350px',
        },
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  const handleDownload = useMemo(
    () => () => {
      const rows = table.getPrePaginationRowModel().rows
      const dataToExport = rows.map(row => {
        const item = row.original
        return {
          ...item,
          isEmpty: item.isEmpty ? t('empty', 'Empty') : t('full', 'Full'),
          isDangerous: item.isDangerous ? t('yes', 'Yes') : t('no', 'No'),
          isReefer: item.isReefer ? t('yes', 'Yes') : t('no', 'No'),
          isDamaged: item.isDamaged ? t('yes', 'Yes') : t('no', 'No'),
        }
      })

      exportToCsv(
        t('storageReport', 'Storage Report'),
        dataToExport,
        dataExportConfig,
        `Storage_Report_${dayjs().format('YYYYMMDD')}.csv`,
      )
    },
    [table, t, dataExportConfig],
  )

  useEffect(() => {
    storageReportUIStore.setHandleDownload(handleDownload)
    return () => storageReportUIStore.setHandleDownload(undefined)
  }, [handleDownload, storageReportUIStore])

  useEffect(() => {
    //TODO: how to ensure that filter is set?
    const loadData = async () => {
      await storageReportUIStore.loadSummary()
    }

    loadData()
  }, [storageReportUIStore])

  return (
    <Stack width='100%' gap={theme => theme.customSpacing.m}>
      <ReportSummaryCard
        title={t('total', 'Total')}
        totalNumber={storageReportUIStore.totalDays}
        numberDescription={t('days', 'Days')}
      />

      {storageReportUIStore.isLoading ? <CircularProgress /> : <TomTable table={table} />}
    </Stack>
  )
})
